import React from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Menu, SwyftxCompact, SwyftxFull } from '@swyftx/aviary/icons/outlined';

import { AppHeaderHelp } from '@global-components/AppHeader/AppHeaderHelp';
import { AppHeaderSettings } from '@global-components/AppHeader/AppHeaderSettings';

import { observer } from 'mobx-react-lite';
import { useSwyftxMarginStore } from 'src/lib/margin';
import { NavigationURLs } from 'src/lib/navigation/types';

import { SwyftxMarginAppHeaderNavItem } from './SwyftxMarginAppHeaderNavItem';
import { SwyftxMarginAppHeaderProfile } from './SwyftxMarginAppHeaderProfile';
import { SwyftxMarginAppHeaderTransfer } from './SwyftxMarginAppHeaderTransfer';

type Props = {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (mobileMenuOpen: boolean) => void;
};

const SwyftxMarginAppHeader: React.FC<Props> = observer(({ mobileMenuOpen, setMobileMenuOpen }) => {
  const { tradeCurrency } = useSwyftxMarginStore();
  return (
    <FlexLayout
      className='flex h-[72px] max-h-[72px] min-h-[72px] w-full border-b border-color-border-main bg-color-background-surface-primary px-16 sm:!h-[56px] sm:!max-h-[56px] sm:!min-h-[56px]'
      alignItems='center'
      justifyContent='space-between'
    >
      {/* Desktop Header */}
      <FlexLayout className='hidden h-full w-full sm:flex' alignItems='center' justifyContent='space-between'>
        <FlexLayout alignItems='center' justifyContent='start' spacing={4} className='h-full'>
          <SwyftxFull className='mr-32 h-[2rem] text-color-text-primary' />
          <SwyftxMarginAppHeaderNavItem
            url={NavigationURLs.MarginAccount}
            title='Account'
            dataTestId='margin-header-account'
          />
          <SwyftxMarginAppHeaderNavItem
            url={NavigationURLs.MarginTradeAsset}
            pathParams={{ asset: tradeCurrency || 'BTC' }}
            title='Trade'
            dataTestId='margin-header-trade'
          />
        </FlexLayout>
        <FlexLayout alignItems='center' justifyContent='end' spacing={8}>
          <SwyftxMarginAppHeaderTransfer variant='outlined' />
          <AppHeaderHelp />
          <AppHeaderSettings />

          <SwyftxMarginAppHeaderProfile />
        </FlexLayout>
      </FlexLayout>

      {/* Mobile Header */}
      <FlexLayout className='flex w-full sm:hidden' alignItems='center' spacing={8}>
        <div className='block sm:hidden'>
          <Button
            id='header-menu'
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            variant='ghost'
            layout='icon'
            leadingIcon={<Menu className='text-color-text-secondary' />}
          />
        </div>

        <FlexLayout className='flex w-full md:hidden' alignItems='center' justifyContent='start' spacing={4}>
          <SwyftxMarginAppHeaderNavItem
            url={NavigationURLs.MarginAccount}
            title='Account'
            dataTestId='margin-header-mobile-account'
          />
          <SwyftxMarginAppHeaderNavItem
            url={NavigationURLs.MarginTrade}
            title='Trade'
            dataTestId='margin-header-mobile-trade'
          />
        </FlexLayout>

        <SwyftxCompact className='h-[4rem] w-[3rem] text-color-text-primary' />
      </FlexLayout>
    </FlexLayout>
  );
});

export { SwyftxMarginAppHeader };
